import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Users, Phone, Trash2, Search, Download, MessageSquare } from "lucide-react";

const STATUS = [
  { value: "active", label: "Active", cls: "bg-emerald-100 text-emerald-900" },
  { value: "contacted", label: "Contacted", cls: "bg-sky-100 text-sky-900" },
  { value: "inactive", label: "Inactive", cls: "bg-slate-200 text-slate-700" },
];

const empty = { name: "", email: "", phone: "", address: "", town: "", skills: "", availability: "", roles: "", status: "active", notes: "" };

export default function Volunteers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("any");
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id, ...} = edit
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (statusFilter && statusFilter !== "any") params.set("status", statusFilter);
      const { data } = await api.get(`/volunteers?${params.toString()}`);
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (v) => { setForm({ ...empty, ...v }); setEditing(v); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = { ...form, num_signs: undefined };
      delete payload.id; delete payload.user_id; delete payload.created_at; delete payload.updated_at; delete payload.last_contact_at;
      if (editing?.id) {
        await api.patch(`/volunteers/${editing.id}`, payload);
        toast.success("Volunteer updated");
      } else {
        await api.post("/volunteers", payload);
        toast.success("Volunteer added");
      }
      close(); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.name}?`)) return;
    try {
      await api.delete(`/volunteers/${v.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const logContact = async (v) => {
    const note = window.prompt(`Log contact for ${v.name} (optional note):`) ?? null;
    if (note === null) return;
    try {
      await api.post(`/volunteers/${v.id}/contact`, { note });
      toast.success("Contact logged");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/volunteers/export", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `volunteers-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red flex items-center gap-2">
            <Users className="h-4 w-4" /> Volunteer Database
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1" data-testid="volunteers-title">Your volunteers</h1>
          <p className="text-slate-600 mt-2">Track everyone helping the campaign. Per-account, private to you.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="volunteers-export-btn" className="h-10 border-2 font-semibold"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          <Button onClick={openNew} data-testid="volunteers-new-btn" className="h-10 bg-flag-red hover:bg-flag-red-hover text-white font-bold"><UserPlus className="h-4 w-4 mr-1.5" /> Add volunteer</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-slate-400" />
          <Input data-testid="volunteers-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, email, skills…" className="h-10 flex-1" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="volunteers-status-filter" className="w-40 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} variant="outline" className="h-10 border-2 font-semibold" data-testid="volunteers-search-btn">Apply</Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="volunteers-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-3 py-3 font-bold">Contact</th>
                <th className="text-left px-3 py-3 font-bold">Town</th>
                <th className="text-left px-3 py-3 font-bold">Skills / roles</th>
                <th className="text-left px-3 py-3 font-bold">Status</th>
                <th className="text-left px-3 py-3 font-bold">Last contact</th>
                <th className="text-right px-3 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500" data-testid="volunteers-empty">No volunteers yet. Add your first.</td></tr>}
              {!loading && rows.map((v) => {
                const s = STATUS.find((x) => x.value === v.status) || STATUS[0];
                return (
                  <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`volunteer-row-${v.id}`}>
                    <td className="px-4 py-3 font-semibold text-flag-navy">{v.name}</td>
                    <td className="px-3 py-3 text-slate-700 whitespace-nowrap">
                      {v.email && <div className="text-xs">{v.email}</div>}
                      {v.phone && <div className="text-xs">{v.phone}</div>}
                    </td>
                    <td className="px-3 py-3 text-slate-700">{v.town || "—"}</td>
                    <td className="px-3 py-3 text-slate-700"><div className="text-xs">{v.skills}</div><div className="text-xs text-slate-500">{v.roles}</div></td>
                    <td className="px-3 py-3"><span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span></td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{v.last_contact_at ? new Date(v.last_contact_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Button variant="outline" onClick={() => logContact(v)} data-testid={`volunteer-contact-${v.id}`} className="h-8 px-2 text-xs border-2 font-semibold"><Phone className="h-3 w-3 mr-1" /> Log contact</Button>
                      <Button variant="outline" onClick={() => openEdit(v)} data-testid={`volunteer-edit-${v.id}`} className="h-8 px-2 text-xs border-2 font-semibold ml-1"><MessageSquare className="h-3 w-3 mr-1" /> Edit</Button>
                      <Button variant="ghost" onClick={() => remove(v)} data-testid={`volunteer-delete-${v.id}`} className="h-8 px-2 text-xs text-flag-red ml-1"><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="volunteer-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit volunteer" : "Add volunteer"}</DialogTitle>
            <DialogDescription>Personal info stays private to your account.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *"><Input data-testid="volunteer-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="volunteer-form-status" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Email"><Input data-testid="volunteer-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" /></Field>
            <Field label="Phone"><Input data-testid="volunteer-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10" /></Field>
            <Field label="Town"><Input data-testid="volunteer-form-town" value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} className="h-10" /></Field>
            <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-10" /></Field>
            <Field label="Skills (comma-separated)" full><Input data-testid="volunteer-form-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="canvassing, phones, data entry" className="h-10" /></Field>
            <Field label="Roles" full><Input value={form.roles} onChange={(e) => setForm({ ...form, roles: e.target.value })} placeholder="canvasser, phonebanker, driver" className="h-10" /></Field>
            <Field label="Availability" full><Input value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="weekends, evenings" className="h-10" /></Field>
            <Field label="Notes" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="border-2 font-semibold">Cancel</Button>
            <Button onClick={save} data-testid="volunteer-form-save" className="bg-flag-red hover:bg-flag-red-hover text-white font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
